import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { NoAccess } from "../equipment/shared/NoAccess";
import EntryFormDialog from "./EntryFormDialog";
import { MANAGE_ROLES, VIEW_ROLES, fmtDate, fmtNum } from "./shared";

export default function EntriesPage() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const { items, loading, page, setPage, totalPages, total, reload } = usePaginatedList("/service-tracker/entries", {});

  if (!VIEW_ROLES.includes(user?.role)) return <NoAccess />;

  return (
    <div className="space-y-6" data-testid="service-tracker-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Service Entries</h2>
          <p className="text-sm text-slate-500">Oil and filter services done on Piling rigs — one entry per workshop visit. {!canManage && "View only."}</p>
        </div>
        {canManage && (
          <Button data-testid="service-tracker-new-entry-button" className="bg-amber-600 hover:bg-amber-700" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New Entry
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <Table data-testid="service-tracker-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead className="text-right">HMR</TableHead>
              <TableHead>Services</TableHead>
              <TableHead>Performed By</TableHead>
              <TableHead>Remarks</TableHead>
              <TableHead>Entered By</TableHead>
              <TableHead>Updated By</TableHead>
              {canManage && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center py-8 text-slate-400" data-testid="service-tracker-empty">No service entries yet.</TableCell></TableRow>
            ) : items.map((e) => (
              <TableRow key={e.id} data-testid={`service-entry-row-${e.id}`} className="align-top">
                <TableCell className="whitespace-nowrap">{fmtDate(e.date)}</TableCell>
                <TableCell className="font-medium whitespace-nowrap">{e.asset_label}</TableCell>
                <TableCell className="text-right font-mono">{fmtNum(e.hmr)}</TableCell>
                <TableCell>
                  <ul className="space-y-0.5 text-sm">
                    {e.lines.map((l, i) => (
                      <li key={i}>{l.service_type} <span className="text-slate-500">· {l.grade} · Qty {fmtNum(l.quantity)}{l.part_code ? ` · ${l.part_code}` : ""}</span></li>
                    ))}
                  </ul>
                </TableCell>
                <TableCell>{e.performed_by || "—"}</TableCell>
                <TableCell className="max-w-[16rem]">{e.remarks || "—"}</TableCell>
                <TableCell className="text-sm whitespace-nowrap" data-testid={`service-entry-entered-by-${e.id}`}>
                  {e.entered_by_name} <span className="text-slate-400">({e.entered_by})</span>
                  <div className="text-xs text-slate-400">{e.entered_at ? new Date(e.entered_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""}</div>
                </TableCell>
                <TableCell className="text-sm text-slate-400" data-testid={`service-entry-updated-by-${e.id}`}>{e.updated_by ? `${e.updated_by_name} (${e.updated_by})` : "—"}</TableCell>
                {canManage && (
                  <TableCell><Button size="icon" variant="ghost" data-testid={`service-entry-edit-${e.id}`} onClick={() => setEditing(e)}><Pencil className="h-4 w-4" /></Button></TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage} testId="service-tracker-pagination" />

      {(showForm || editing) && <EntryFormDialog entry={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); reload(); }} />}
    </div>
  );
}
